/**
 * Pricing Modal Context
 *
 * Provides global state management for the pricing modal dialog.
 * Allows any component in the application to open or close the pricing modal
 * and check its current state.
 */

import { createContext, useContext, useState, ReactNode } from 'react';

/**
 * Context type for pricing modal state and controls
 *
 * @property {boolean} isOpen - Whether the pricing modal is currently open
 * @property {() => void} openPricingModal - Function to open the pricing modal
 * @property {() => void} closePricingModal - Function to close the pricing modal
 */
interface PricingModalContextType {
  isOpen: boolean;
  openPricingModal: () => void;
  closePricingModal: () => void;
}

const PricingModalContext = createContext<PricingModalContextType | undefined>(undefined);

/**
 * Hook to access pricing modal state and controls
 *
 * @returns {PricingModalContextType} The pricing modal context with isOpen state and open/close functions
 * @throws {Error} If used outside of PricingModalProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isOpen, openPricingModal, closePricingModal } = usePricingModal();
 *
 *   return (
 *     <div>
 *       <button onClick={openPricingModal}>View Pricing</button>
 *       {isOpen && <p>Modal is open</p>}
 *     </div>
 *   );
 * }
 * ```
 */
export const usePricingModal = () => {
  const context = useContext(PricingModalContext);
  if (!context) {
    throw new Error('usePricingModal must be used within a PricingModalProvider');
  }
  return context;
};

/**
 * Props for PricingModalProvider component
 *
 * @property {ReactNode} children - Child components to be wrapped by the provider
 */
interface PricingModalProviderProps {
  children: ReactNode;
}

/**
 * Provider component for pricing modal context
 *
 * Wraps the application (or part of it) to provide pricing modal state
 * and controls to all descendant components via the usePricingModal hook.
 *
 * @param {PricingModalProviderProps} props - Component props
 * @returns {JSX.Element} Provider component with pricing modal context
 *
 * @example
 * ```tsx
 * <PricingModalProvider>
 *   <App />
 * </PricingModalProvider>
 * ```
 */
export const PricingModalProvider = ({ children }: PricingModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const openPricingModal = () => setIsOpen(true);
  const closePricingModal = () => setIsOpen(false);

  return (
    <PricingModalContext.Provider value={{ isOpen, openPricingModal, closePricingModal }}>
      {children}
    </PricingModalContext.Provider>
  );
};